import { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { Modal, Form, Button, Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import UserContext from 'context/user-context'
import { errorMessage } from 'admin/shared/errors'
import {
  useCreateMunicipalityAdminUser,
  useUpdateMunicipalityAdminUser
} from 'api/hooks/use-municipality-admin-users'

// 'none' revokes report (Datum) access entirely; the other values set the role
// while keeping access on. The server clears datum_role when access is off.
const REPORTS_ROLES = [
  { value: 'none', label: 'No report access' },
  { value: 'datum_user', label: 'User' },
  { value: 'datum_admin', label: 'Admin (sees all reports)' }
]

const buildState = (user) => ({
  first_name: user?.first_name ?? '',
  last_name: user?.last_name ?? '',
  email: user?.email ?? '',
  // New users default to report access on; edits reflect the user's current flag.
  has_datum_app: user ? !!user.has_datum_app : true,
  datum_role: user?.datum_role ?? 'datum_user',
  is_user_admin: !!user?.is_user_admin
})

// Invite a new user (no `user` prop) or edit an existing one. Invited emails
// that already belong to a municipality user just get Datum access enabled —
// the server decides, so the flows share this form.
const UserFormModal = ({ user, show, onHide }) => {
  const currentUser = useContext(UserContext)
  const isEdit = !!user
  const isSelf = isEdit && user.id === currentUser?.id
  const [form, setForm] = useState(() => buildState(user))
  const [errorMsg, setErrorMsg] = useState(null)

  useEffect(() => {
    if (show) {
      setForm(buildState(user))
      setErrorMsg(null)
    }
  }, [show, user?.id])

  const mutationOptions = {
    options: {
      onSuccess: () => {
        toast.success(isEdit ? 'User updated' : 'User added')
        onHide()
      },
      onError: (err) => setErrorMsg(errorMessage(err, isEdit ? 'Failed to update user' : 'Failed to add user'))
    }
  }
  const createUser = useCreateMunicipalityAdminUser(mutationOptions)
  const updateUser = useUpdateMunicipalityAdminUser(mutationOptions)
  const saving = createUser.isLoading || updateUser.isLoading

  const setField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  // The single Reports Role control drives both access (has_datum_app) and the
  // role: 'none' turns access off, any other value turns it on with that role.
  const reportsRole = form.has_datum_app ? form.datum_role : 'none'
  const handleReportsRoleChange = (e) => {
    const value = e.target.value
    setForm((prev) =>
      value === 'none'
        ? { ...prev, has_datum_app: false }
        : { ...prev, has_datum_app: true, datum_role: value }
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrorMsg(null)
    if (isEdit) {
      const { email, ...params } = form
      updateUser.mutate({ userId: user.id, params })
    } else {
      createUser.mutate(form)
    }
  }

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit User' : 'Add User'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

          <Form.Group className="mb-3" controlId="mua-first-name">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              value={form.first_name}
              onChange={setField('first_name')}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="mua-last-name">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              value={form.last_name}
              onChange={setField('last_name')}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="mua-email">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={form.email}
              onChange={setField('email')}
              disabled={isEdit}
              readOnly={isEdit}
              required
            />
            <Form.Text className="text-muted">
              {isEdit
                ? "Email is tied to the user's sign-in identity and can't be changed."
                : 'Must use your organization’s email domain. If this person already has an account, report access is enabled on it.'}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="mua-datum-role">
            <Form.Label>Reports Role</Form.Label>
            <Form.Select value={reportsRole} onChange={handleReportsRoleChange}>
              {REPORTS_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-1" controlId="mua-is-user-admin">
            <Form.Check
              type="switch"
              id="mua-is-user-admin-switch"
              label="User Admin (can manage users)"
              checked={form.is_user_admin}
              onChange={setField('is_user_admin')}
              disabled={isSelf}
            />
            {isSelf && (
              <Form.Text className="text-muted">
                You can&apos;t change your own User Admin access.
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={onHide}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Add User')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

UserFormModal.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    has_datum_app: PropTypes.bool,
    datum_role: PropTypes.string,
    is_user_admin: PropTypes.bool
  }),
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired
}

export default UserFormModal
